import { useState, useEffect } from "react";
import useClassSchedules, { daysToLabel, formatDateRangeKorean } from "@/hooks/useClassSchedules";

const FAQ = [
  {
    q: "히라가나를 몰라도 되나요?",
    a: "네, 1주차에 히라가나부터 함께 시작합니다. 완전 초보를 기준으로 설계된 수업이에요.",
  },
  {
    q: "결석하면 어떻게 되나요?",
    a: "모든 수업은 녹화되어 수강생에게 제공되며, 다음 수업 전 간단한 개별 안내가 있어요.",
  },
  {
    q: "숙제는 얼마나 있나요?",
    a: "매 회 20~30분 정도의 짧은 복습·단어 암기 과제가 있습니다. 부담되지 않는 수준이에요.",
  },
  {
    q: "수업은 녹화되나요?",
    a: "네, 모든 회차가 녹화됩니다. 다시 듣기용으로 반복 학습에 활용하실 수 있어요.",
  },
  {
    q: "환불은 어떻게 되나요?",
    a: "개강 전 100% 환불, 개강 후 진행 회차에 따라 안내드립니다. 수강신청 시 자세히 안내해요.",
  },
];

export default function CourseInfo() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const [activeCohortId, setActiveCohortId] = useState<number | null>(null);
  const { schedules: classSchedules } = useClassSchedules(activeCohortId);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("active_cohort_id");
      if (raw) setActiveCohortId(Number(raw));
    } catch {
      // storage unavailable
    }
  }, []);

  // Build dynamic info rows
  const classRows = classSchedules.map((s) => ({
    type: "sub" as const,
    label: s.name,
    value: `${formatDateRangeKorean(s.start_date, s.end_date)} ${s.start_time}~${s.end_time}`,
  }));

  const dayLabels = classSchedules.map((s) => daysToLabel(s.days)).filter(Boolean);
  const totalDays = dayLabels.join(" / ");
  const totalClasses = classSchedules.length * 16; // rough estimate

  const timeInfo = classSchedules.length > 0
    ? `${classSchedules[0].start_time}~${classSchedules[0].end_time}`
    : "20:00~21:15";

  const INFO = [
    { type: "section" as const, label: "기간 · 일정" },
    ...classRows,
    { type: "item" as const, label: "횟수", value: totalDays ? `주 2회 (${totalDays})` : "주 2회" },
    { type: "item" as const, label: "시간", value: `${timeInfo} (75분)` },
    { type: "item" as const, label: "방식", value: "온라인 (Zoom)" },
    { type: "item" as const, label: "정원", value: "최대 5명 소수정예" },
    { type: "item" as const, label: "수강료", value: "8회 기준 월 12만원 (교재 별도)" },
    { type: "item" as const, label: "준비물", value: "Zoom 이용 가능 전자기기, 교재, 필기도구" },
  ];

  return (
    <section id="info" className="py-10 md:py-28 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* 수업 안내 */}
        <div className="bg-background-100 border border-background-200 rounded-xl p-4 md:p-7">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
            <span className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-information-line"></i>
            </span>
            수업 안내
          </h3>
          <dl className="mt-5 divide-y divide-background-200">
            {INFO.map((row) => {
              if (row.type === "section") {
                return (
                  <div key={row.label} className="py-2 sm:py-2.5">
                    <dt className="text-sm font-bold text-foreground-950">{row.label}</dt>
                  </div>
                );
              }
              if (row.type === "sub") {
                return (
                  <div key={row.label} className="py-2 grid grid-cols-3 sm:grid-cols-4 gap-3 text-sm pl-4">
                    <dt className="col-span-1 text-foreground-600 break-all">{row.label}</dt>
                    <dd className="col-span-2 sm:col-span-3 text-foreground-900 break-all">{row.value}</dd>
                  </div>
                );
              }
              return (
                <div key={row.label} className="py-2 sm:py-2.5 grid grid-cols-3 sm:grid-cols-4 gap-3 text-sm">
                  <dt className="col-span-1 font-semibold text-foreground-700 break-all">{row.label}</dt>
                  <dd className="col-span-2 sm:col-span-3 text-foreground-900 break-all">{row.value}</dd>
                </div>
              );
            })}
          </dl>
        </div>

        {/* 강사 소개 */}
        <div className="bg-background-100 border border-background-200 rounded-xl p-4 md:p-7 flex flex-col">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
            <span className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-user-star-line"></i>
            </span>
            강사 소개
          </h3>

          <div className="mt-5 flex flex-col items-center text-center">
            {/* 강사 이미지 */}
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-2 border-background-200">
              <img
                src="https://storage.helloreaddy.io/project_files/801b3742-73ea-4115-b79c-b555eb82e9ba/7562b882-a560-44b0-afc9-a539887c6ced_compressed_2.webp"
                alt="강사 사진"
                className="w-full h-full object-cover"
              />
            </div>
            <h4 className="mt-3 font-heading text-base sm:text-lg font-bold text-foreground-950">이의현</h4>
            <p className="mt-0.5 text-xs text-foreground-600">고려대학교 경영학과 재학</p>
          </div>

          <ul className="mt-5 space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-foreground-800">
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
              5년간의 과외 지도 경험
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
              20회 이상 일본 방문
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
              일본인 원어민과의 지속적 교류를 통한 <br />실제 언어 감각
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
              초보자 맞춤 쉬운 설명과 학습자료
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
              이해를 바탕으로 직접 말하는 수업
            </li>
          </ul>
        </div>

        {/* FAQ */}
        <div id="faq" className="bg-background-100 border border-background-200 rounded-xl p-4 md:p-7">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
            <span className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-question-line"></i>
            </span>
            자주 묻는 질문
          </h3>
          <div className="mt-5 divide-y divide-background-200">
            {FAQ.map((item, idx) => (
              <div key={item.q} className="py-3">
                <button
                  className="w-full flex items-center justify-between text-left cursor-pointer"
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                >
                  <span className="text-xs sm:text-sm font-semibold text-foreground-900 pr-3">
                    Q{idx + 1}. {item.q}
                  </span>
                  <span className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 text-xs shrink-0">
                    <i className={openIdx === idx ? "ri-subtract-line" : "ri-add-line"}></i>
                  </span>
                </button>
                {openIdx === idx && (
                  <p className="mt-2.5 text-xs sm:text-sm text-foreground-700 leading-relaxed">
                    {item.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}